import React from 'react';
import {
  Datagrid,
  Edit,
  EditButton,
  FormDataConsumer,
  FunctionField,
  List,
  NumberInput,
  Resource,
  Show,
  SimpleForm,
  SimpleShowLayout,
  TextField,
} from 'react-admin';

const ratio = (label, readOnly = false) => ({
  label,
  scale: 'ratio',
  readOnly,
});
const score = (label) => ({ label, scale: 'score', readOnly: false });

export const CIRCULARITY_DEFAULT_DEFINITIONS = {
  recovery_rate: ratio('Recovery rate', true),
  eol_recycling: ratio('End-of-life recycling share'),
  eol_reuse: ratio('End-of-life reuse share'),
  eol_landfill: ratio('End-of-life landfill share'),
  band_mci_low_upper: ratio('MCI: low upper threshold'),
  band_mci_medium_upper: ratio('MCI: medium upper threshold'),
  band_recycled_content_low_upper: ratio(
    'Recycled content: low upper threshold',
  ),
  band_recycled_content_medium_upper: ratio(
    'Recycled content: medium upper threshold',
  ),
  band_recyclability_low_upper: ratio(
    'Recyclability: low upper threshold',
  ),
  band_recyclability_medium_upper: ratio(
    'Recyclability: medium upper threshold',
  ),
  band_eol_low_upper: ratio('End-of-life: low upper threshold'),
  band_eol_medium_upper: ratio('End-of-life: medium upper threshold'),
  band_collection_low_upper: ratio('Collection: low upper threshold'),
  band_collection_medium_upper: ratio('Collection: medium upper threshold'),
  band_durability_low_upper: ratio('Durability: low upper threshold'),
  band_durability_medium_upper: ratio('Durability: medium upper threshold'),
  band_repairability_low_upper: score(
    'Repairability: low upper threshold',
  ),
  band_repairability_medium_upper: score(
    'Repairability: medium upper threshold',
  ),
};

const definitionFor = (key) =>
  CIRCULARITY_DEFAULT_DEFINITIONS[key] || {
    label: key,
    scale: 'ratio',
    readOnly: true,
  };

export const formatCircularityDefault = (key, value) => {
  if (value === null || value === undefined || value === '') return '—';
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '—';
  return definitionFor(key).scale === 'ratio'
    ? `${numericValue * 100}%`
    : `${numericValue}`;
};

export const parseCircularityDefault = (key, value) => {
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '')
  ) {
    return null;
  }
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;
  return definitionFor(key).scale === 'ratio'
    ? numericValue / 100
    : numericValue;
};

export const isCircularityDefaultEditable = (key) =>
  !definitionFor(key).readOnly;

const DefaultNameField = () => (
  <FunctionField
    label="Setting"
    render={(record) => definitionFor(record.default_key).label}
  />
);

const DefaultValueField = () => (
  <FunctionField
    label="Value"
    render={(record) =>
      formatCircularityDefault(record.default_key, record.default_value)
    }
  />
);

const CircularityDefaultsList = (props) => (
  <List {...props} bulkActionButtons={false}>
    <Datagrid rowClick="show">
      <DefaultNameField />
      <TextField source="default_key" label="Configuration key" />
      <DefaultValueField />
      <FunctionField
        label="Actions"
        sortable={false}
        render={(record) =>
          isCircularityDefaultEditable(record.default_key) ? (
            <EditButton record={record} />
          ) : null
        }
      />
    </Datagrid>
  </List>
);

const CircularityDefaultsShow = (props) => (
  <Show {...props}>
    <SimpleShowLayout>
      <DefaultNameField />
      <TextField source="default_key" label="Configuration key" />
      <DefaultValueField />
    </SimpleShowLayout>
  </Show>
);

const CircularityDefaultsEdit = (props) => (
  <Edit {...props} undoable={false}>
    <SimpleForm>
      <TextField source="default_key" label="Configuration key" />
      <FormDataConsumer>
        {({ formData }) => {
          const definition = definitionFor(formData.default_key);
          return (
            <NumberInput
              source="default_value"
              label={
                definition.scale === 'ratio'
                  ? 'Value (%)'
                  : 'Value (0–6 score)'
              }
              min={0}
              max={definition.scale === 'ratio' ? 100 : 6}
              step={definition.scale === 'ratio' ? 0.1 : 0.01}
              disabled={definition.readOnly}
              format={(value) =>
                definition.scale === 'ratio' && value !== undefined
                  ? Number(value) * 100
                  : value
              }
              parse={(value) =>
                parseCircularityDefault(formData.default_key, value)
              }
            />
          );
        }}
      </FormDataConsumer>
    </SimpleForm>
  </Edit>
);

const circularityDefaultsResource = () => (
  <Resource
    name="circularityDefaults"
    options={{ label: 'Circularity Defaults', menuGroup: 'Bcome' }}
    list={CircularityDefaultsList}
    show={CircularityDefaultsShow}
    edit={CircularityDefaultsEdit}
  />
);

export default circularityDefaultsResource;
